async function getJson(f) {
    const response = await f;

    if (response.status !== 200) {
        throw new Error('An unexpected error has occurred. Please try again later.');
    }

    const json = await response.json();

    return json;
}

export default {
    async getProductOptions(blueprint) {
        let ocapiClient = blueprint.custom['v-ocapi-client'];

        if (this.options.ocapiClientId) {
            ocapiClient = this.options.ocapiClientId;
        }

        let ocapiBase = blueprint.custom['v-ocapi-url'];

        if (this.options.ocapiRootUrl) {
            ocapiBase = this.options.ocapiRootUrl;
        }

        const product = await getJson(fetch(`${ocapiBase}/products/${blueprint.styleCode}?expand=options&client_id=${ocapiClient}`, {
            method: 'GET',
            mode: 'cors',
        }));

        return product;
    },

    async getListItem(blueprint, lineItemId) {
        let ocapiClient = blueprint.custom['v-ocapi-client'];

        if (this.options.ocapiClientId) {
            ocapiClient = this.options.ocapiClientId;
        }

        let ocapiBase = blueprint.custom['v-ocapi-url'];

        if (this.options.ocapiRootUrl) {
            ocapiBase = this.options.ocapiRootUrl;
        }

        // Stage 1: Login and get tokens.
        let loginResponse = await fetch(`${ocapiBase}/customers/auth?client_id=${ocapiClient}`, {
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                type: 'session',
            }),
            method: 'POST',
            mode: 'cors',
        });

        if (loginResponse.status !== 200) {
            loginResponse = await fetch(`${ocapiBase}/customers/auth?client_id=${ocapiClient}`, {
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    type: 'guest',
                }),
                method: 'POST',
                mode: 'cors',
            });
        }

        if (loginResponse.status !== 200) {
            throw new Error('An unexpected error has occurred. Please try again later.');
        }

        // Grab the bearer token.
        const customer = await loginResponse.json();
        const token = [...loginResponse.headers].find((x) => x[0] === 'authorization')[1];

        // Stage 2: Get lists.
        const lists = await getJson(fetch(`${ocapiBase}/customers/${customer.customer_id}/product_lists?client_id=${ocapiClient}`, {
            method: 'GET',
            headers: {
                Authorization: token,
            },
            mode: 'cors',
        }));

        if (lists == null || lists.data == null || lists.data.length === 0) {
            return null;
        }

        const wishlist = lists.data.find((x) => x.type === 'wish_list');

        if (wishlist == null) {
            return null;
        }

        // eslint-disable-next-line max-len
        const items = await getJson(fetch(`${ocapiBase}/customers/${customer.customer_id}/product_lists/${wishlist.id}/items?expand=product&client_id=${ocapiClient}`, {
            method: 'GET',
            headers: {
                Authorization: token,
            },
            mode: 'cors',
        }));

        if (items == null || items.data == null) {
            return null;
        }

        const match = items.data.find((x) => {
            if (x.id === lineItemId) {
                return true;
            }

            try {
                const config = JSON.parse(x.c_wedgeCustomizedProductJSON);
                if (config.lineItemUUID === lineItemId) {
                    return true;
                }
            } catch (e) {
                // Nothing.
            }

            return false;
        });

        if (match && match.c_wedgeCustomizedProductJSON) {
            // eslint-disable-next-line no-unused-vars
            const config = JSON.parse(match.c_wedgeCustomizedProductJSON);

            let agsData = null;

            if (config && config.wedgeWorksAGSData) {
                agsData = config.wedgeWorksAGSData;
            }

            return {
                productId: match.product_id,
                configuration: agsData,
            };
        }

        return match;
    },

    async getLineItem(blueprint, lineItemId) {
        let ocapiClient = blueprint.custom['v-ocapi-client'];

        if (this.options.ocapiClientId) {
            ocapiClient = this.options.ocapiClientId;
        }

        let ocapiBase = blueprint.custom['v-ocapi-url'];

        if (this.options.ocapiRootUrl) {
            ocapiBase = this.options.ocapiRootUrl;
        }

        // Stage 1: Login and get tokens.
        let loginResponse = await fetch(`${ocapiBase}/customers/auth?client_id=${ocapiClient}`, {
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                type: 'session',
            }),
            method: 'POST',
            mode: 'cors',
        });

        if (loginResponse.status !== 200) {
            loginResponse = await fetch(`${ocapiBase}/customers/auth?client_id=${ocapiClient}`, {
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    type: 'guest',
                }),
                method: 'POST',
                mode: 'cors',
            });
        }

        if (loginResponse.status !== 200) {
            throw new Error('An unexpected error has occurred. Please try again later.');
        }

        // Grab the bearer token.
        const customer = await loginResponse.json();
        const token = [...loginResponse.headers].find((x) => x[0] === 'authorization')[1];

        // Stage 2: Get baskets.
        const baskets = await getJson(fetch(`${ocapiBase}/customers/${customer.customer_id}/baskets?client_id=${ocapiClient}`, {
            method: 'GET',
            headers: {
                Authorization: token,
            },
            mode: 'cors',
        }));

        // eslint-disable-next-line no-restricted-syntax
        if (baskets.baskets) {
            // eslint-disable-next-line no-restricted-syntax
            for (const basket of baskets.baskets) {
                if (basket.product_items) {
                    const match = basket.product_items.find((x) => x.item_id === lineItemId);

                    if (match) {
                        return {
                            productId: match.product_id,
                            configuration: match.c_wedgeWorksAGSData,
                        };
                    }
                }
            }
        }

        return null;
    },

    /**
     * Remove line item from cart.
     */
    async removeLineItem(controller, lineItemId) {
        const { blueprint } = controller;

        let ocapiClient = blueprint.custom['v-ocapi-client'];

        if (this.options.ocapiClientId) {
            ocapiClient = this.options.ocapiClientId;
        }

        let ocapiBase = blueprint.custom['v-ocapi-url'];

        if (this.options.ocapiRootUrl) {
            ocapiBase = this.options.ocapiRootUrl;
        }

        // Stage 1: Login and get tokens.
        let loginResponse = await fetch(`${ocapiBase}/customers/auth?client_id=${ocapiClient}`, {
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                type: 'session',
            }),
            method: 'POST',
            mode: 'cors',
        });

        if (loginResponse.status !== 200) {
            loginResponse = await fetch(`${ocapiBase}/customers/auth?client_id=${ocapiClient}`, {
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    type: 'guest',
                }),
                method: 'POST',
                mode: 'cors',
            });
        }

        if (loginResponse.status !== 200) {
            throw new Error('An unexpected error has occurred. Please try again later.');
        }

        // Grab the bearer token.
        const customer = await loginResponse.json();
        const token = [...loginResponse.headers].find((x) => x[0] === 'authorization')[1];

        // Stage 2: Get baskets.
        const baskets = await getJson(fetch(`${ocapiBase}/customers/${customer.customer_id}/baskets?client_id=${ocapiClient}`, {
            method: 'GET',
            headers: {
                Authorization: token,
            },
            mode: 'cors',
        }));

        let fromBasket = null;

        // eslint-disable-next-line no-restricted-syntax
        if (baskets.baskets) {
            // eslint-disable-next-line no-restricted-syntax
            for (const basket of baskets.baskets) {
                if (basket.product_items) {
                    const match = basket.product_items.find((x) => x.item_id === lineItemId);

                    if (match) {
                        fromBasket = basket.basket_id;
                    }
                }
            }
        }

        if (fromBasket) {
            // eslint-disable-next-line no-unused-vars
            const result = await getJson(fetch(`${ocapiBase}/baskets/${fromBasket}/items/${lineItemId}?client_id=${ocapiClient}`, {
                method: 'POST',
                headers: {
                    Authorization: token,
                    'Content-Type': 'application/json',
                    'x-dw-http-method-override': 'DELETE',
                },
                mode: 'cors',
            }));
        }

        return null;
    },

    async generateCartPayload(controller, recipe) {
        const items = [];

        const product = {
        };

        const descriptionData = this.formatDescriptionData(controller, recipe);

        // Already stringified.
        product.payload = descriptionData;

        items.push(product);

        return items;
    },

    // eslint-disable-next-line no-unused-vars
    async sendToCart(controller, lineItemId, recipe) {
        const payload = await this.generateCartPayload(controller, recipe);

        return payload;
    },

    async sendToCartDirect(controller, lineItemId, alwaysNew) {
        const payload = await this.generateCartPayload(controller);

        let ocapiClient = controller.blueprint.custom['v-ocapi-client'];

        if (this.options.ocapiClientId) {
            ocapiClient = this.options.ocapiClientId;
        }

        let ocapiBase = controller.blueprint.custom['v-ocapi-url'];

        if (this.options.ocapiRootUrl) {
            ocapiBase = this.options.ocapiRootUrl;
        }

        // Stage 1: Login and get tokens.
        let loginResponse = await fetch(`${ocapiBase}/customers/auth?client_id=${ocapiClient}`, {
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                type: 'session',
            }),
            method: 'POST',
            mode: 'cors',
        });

        if (loginResponse.status !== 200) {
            loginResponse = await fetch(`${ocapiBase}/customers/auth?client_id=${ocapiClient}`, {
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    type: 'guest',
                }),
                method: 'POST',
                mode: 'cors',
            });
        }

        if (loginResponse.status !== 200) {
            throw new Error('An unexpected error has occurred. Please try again later.');
        }

        // Grab the bearer token.
        const customer = await loginResponse.json();
        const token = [...loginResponse.headers].find((x) => x[0] === 'authorization')[1];

        // Stage 2: Get baskets.
        let baskets = await getJson(fetch(`${ocapiBase}/customers/${customer.customer_id}/baskets?client_id=${ocapiClient}`, {
            method: 'GET',
            headers: {
                Authorization: token,
            },
            mode: 'cors',
        }));

        // Need to make a basket.
        if (baskets.total === 0) {
            await getJson(fetch(`${ocapiBase}/baskets?client_id=${ocapiClient}`, {
                method: 'POST',
                headers: {
                    Authorization: token,
                },
                mode: 'cors',
            }));

            baskets = await getJson(fetch(`${ocapiBase}/customers/${customer.customer_id}/baskets?client_id=${ocapiClient}`, {
                method: 'GET',
                headers: {
                    Authorization: token,
                },
                mode: 'cors',
            }));
        }

        if (baskets.baskets?.length < 1) {
            throw new Error('An unexpected error has occurred. Please try again later.');
        }

        const basketId = baskets.baskets[0].basket_id;

        let createNew = true;

        if (lineItemId?.length > 0) {
            const firstBasket = baskets.baskets[0];

            if (firstBasket.product_items) {
                const match = firstBasket.product_items.find((x) => x.item_id === lineItemId);

                if (match) {
                    if (this.options.testForceNew) {
                        // For debugging only.
                        createNew = true;
                    } else {
                        createNew = false;
                    }
                }
            }
        }

        if (alwaysNew) {
            createNew = true;
        }

        // Stage 3: Add items.
        if (createNew === false) {
            // Patch an existing line item.

            // eslint-disable-next-line no-unused-vars
            const result = await getJson(fetch(`${ocapiBase}/baskets/${basketId}/items/${lineItemId}?client_id=${ocapiClient}`, {
                method: 'POST',
                headers: {
                    Authorization: token,
                    'Content-Type': 'application/json',
                    'x-dw-http-method-override': 'PATCH',
                },
                body: JSON.stringify(payload[0]),
                mode: 'cors',
            }));

            return lineItemId;
        }

        // Make a new one.

        // eslint-disable-next-line no-unused-vars
        const result = await getJson(fetch(`${ocapiBase}/baskets/${basketId}/items?client_id=${ocapiClient}`, {
            method: 'POST',
            headers: {
                Authorization: token,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
            mode: 'cors',
        }));

        if (result?.product_items?.length > 0) {
            const last = result.product_items[result.product_items.length - 1];

            return last.item_id;
        }

        throw new Error('An unexpected error has occurred. Please try again later.');
    },
};
